import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useParams, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import ArticlePage from "@/pages/ArticlePage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import PapiamentoPage from "@/pages/PapiamentoPage";
import ObituariesPage from "@/pages/ObituariesPage";
import AboutPage from "@/pages/AboutPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import SponsorPage from "@/pages/SponsorPage";
import CruiseSchedulePage from "@/pages/CruiseSchedulePage";
import CarnivalHubPage from "@/pages/CarnivalHubPage";
import CarnivalIslandPage from "@/pages/CarnivalIslandPage";
import CarnivalMarqueePage from "@/pages/CarnivalMarqueePage";
import HistoryHubPage from "@/pages/HistoryHubPage";
import HistoryPhotoPage from "@/pages/HistoryPhotoPage";
import AdvertisePage from "@/pages/AdvertisePage";
import AdvertiseTermsPage from "@/pages/AdvertiseTermsPage";
import AdvertiseBookPage from "@/pages/AdvertiseBookPage";
import AdvertiserLoginPage from "@/pages/AdvertiserLoginPage";
import AdvertiserSetPasswordPage from "@/pages/AdvertiserSetPasswordPage";
import AdvertiserDashboardPage from "@/pages/AdvertiserDashboardPage";
import SeoLandingPage from "@/pages/SeoLandingPage";
import StoryPage from "@/pages/StoryPage";
import { FloatingSubscribeCTA } from "@/components/FloatingSubscribeCTA";
import { SubscribeModalHost } from "@/components/SubscribeModalHost";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { api } from "@/lib/api";
import { SEO_PAGES } from "@/lib/seoLandingPages";
import { captureReferralFromUrl } from "@/lib/referral";

// Reserved top-level paths that must NEVER be treated as island slugs
// even if someone accidentally names an island "admin". Static SEO
// landing pages are also reserved so they win over the dynamic
// /:islandSlug route.
const RESERVED = new Set([
  "article", "terms", "about", "admin", "sponsor", "advertise", "advertiser",
  "story", "verhaal", "historia", "carnival", "history",
  ...SEO_PAGES.map((p) => p.slug),
]);

// Valid island slugs used to detect HomePage-owned routes below.
const ISLAND_SLUGS = new Set(["aruba", "curacao", "bonaire", "stmaarten"]);

// True iff `pathname` is a route rendered by HomePage — either `/` or
// `/:islandSlug` or `/:islandSlug/:category` (NOT `/:islandSlug/cruise-schedule`
// or `/:islandSlug/carnival`, which are separate pages). HomePage owns its
// own scroll restoration via sessionStorage (see HomePage.jsx L194-295); the
// global ScrollToTop below deliberately skips these paths so back-navigation
// restore keeps working.
const isHomePagePath = (pathname) => {
  if (pathname === "/") return true;
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return true;
  if (!ISLAND_SLUGS.has(parts[0])) return false;
  // /:islandSlug            → HomePage
  // /:islandSlug/:category  → HomePage (but NOT cruise-schedule or carnival)
  if (parts.length === 1) return true;
  if (parts.length === 2 && parts[1] !== "cruise-schedule" && parts[1] !== "carnival" && parts[1] !== "history") return true;
  return false;
};

// Global scroll-to-top on route change. Fixes the "new page opens at
// the previous page's scroll position" bug (e.g. clicking Full port
// schedule from a scrolled-down homepage was landing the cruise page
// mid-way). HomePage is exempted because it does its own manual
// restore from sessionStorage for the back-navigation case.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isHomePagePath(pathname)) return;
    // "instant" avoids a jarring smooth-scroll animation on route change.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (_) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

// Referral attribution — captures `?ref=fw` (or any future channel
// mapped in lib/referral.js) from the URL and stashes the resulting
// `subscribe_source` in sessionStorage. Subscribe modals read it at
// submit time. Runs on every route change so a forwarded link that
// deep-links to /aruba still gets attributed correctly.
function ReferralCapture() {
  const { search } = useLocation();
  useEffect(() => {
    captureReferralFromUrl();
  }, [search]);
  return null;
}

/**
 * Dynamic island route guard.
 * URL params: `/:islandSlug` and `/:islandSlug/:category`.
 * On mount fetches active islands from GET /api/islands. If the slug
 * matches an active island → render <HomePage initialIsland={slug} />.
 * Otherwise → render a minimal 404 page.
 */
function IslandRouteGuard() {
  const { islandSlug } = useParams();
  const [state, setState] = useState({ status: "loading", validSlugs: null });

  useEffect(() => {
    let cancelled = false;
    api
      .get("/islands")
      .then((r) => {
        if (cancelled) return;
        const slugs = new Set((r.data || []).map((d) => d.slug));
        setState({ status: "ready", validSlugs: slugs });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "ready", validSlugs: new Set() });
      });
    return () => { cancelled = true; };
  }, []);

  if (RESERVED.has(islandSlug)) return <NotFound />;
  if (state.status === "loading") return null;  // brief blank while resolving
  if (!state.validSlugs.has(islandSlug)) return <NotFound />;
  return <HomePage initialIsland={islandSlug} />;
}

function NotFound() {
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "1rem",
        padding: "2rem",
        textAlign: "center",
      }}
      data-testid="not-found-page"
    >
      <h1 style={{ fontSize: "2rem", margin: 0 }}>404 — Not Found</h1>
      <p style={{ color: "#6B7280" }}>
        We couldn&apos;t find that island.
      </p>
      <Link to="/" style={{ color: "#0AADA0" }}>← Back to IslandsDaily</Link>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ReferralCapture />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/article/:id" element={<ArticlePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/papiamento" element={<PapiamentoPage />} />
          <Route path="/obituaries" element={<ObituariesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/sponsor" element={<SponsorPage />} />
          <Route path="/advertise" element={<AdvertisePage />} />
          <Route path="/advertise/terms" element={<AdvertiseTermsPage />} />
          <Route path="/advertise/book" element={<AdvertiseBookPage />} />
          <Route path="/advertiser/login" element={<AdvertiserLoginPage />} />
          <Route path="/advertiser/set-password" element={<AdvertiserSetPasswordPage />} />
          <Route path="/advertiser/dashboard" element={<AdvertiserDashboardPage />} />
          {/* Phase 2 daily top-stories — one page per article per language */}
          <Route path="/story/:idSlug" element={<StoryPage lang="en" />} />
          <Route path="/verhaal/:idSlug" element={<StoryPage lang="nl" />} />
          <Route path="/historia/:idSlug" element={<StoryPage lang="es" />} />
          {/* Static SEO landing pages — 6 topics × 3 langs */}
          {SEO_PAGES.map((p) => (
            <Route
              key={p.slug}
              path={`/${p.slug}`}
              element={<SeoLandingPage slug={p.slug} />}
            />
          ))}
          {/* Dynamic island routes — validated against /api/islands */}
          <Route path="/carnival" element={<CarnivalHubPage />} />
          <Route path="/carnival/:eventSlug" element={<CarnivalMarqueePage />} />
          <Route path="/:islandSlug/carnival" element={<CarnivalIslandPage />} />
          <Route path="/history" element={<HistoryHubPage />} />
          <Route path="/:islandSlug/history" element={<HistoryHubPage />} />
          <Route path="/history/:slug" element={<HistoryPhotoPage />} />
          <Route path="/:islandSlug/cruise-schedule" element={<CruiseSchedulePage />} />
          <Route path="/:islandSlug" element={<IslandRouteGuard />} />
          <Route path="/:islandSlug/:category" element={<IslandRouteGuard />} />
        </Routes>
        <FloatingSubscribeCTA />
        <MobileStickyBar />
        <SubscribeModalHost />
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
    </div>
  );
}

export default App;
